import React, { useEffect, useMemo, useRef, useState } from "react";
import { toAttachmentUrl } from "../../constants/avatar";
import { THEMES } from "../../constants/themes";

const reactionChoices = [
  String.fromCodePoint(0x1f44d),
  String.fromCodePoint(0x2764, 0xfe0f),
  String.fromCodePoint(0x1f602),
  String.fromCodePoint(0x1f525),
  String.fromCodePoint(0x1f62e)
];

const formatTime = (dateString) =>
  dateString ? new Date(dateString).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";

const isImage = (mimeType = "") => mimeType.startsWith("image/");
const IMAGE_FILE_EXT_PATTERN = /\.(avif|bmp|gif|heic|heif|jpe?g|png|svg|webp)$/i;
const URL_LIKE_PATTERN = /^(https?:\/\/|\/|[a-z0-9_-]{12,}\.[a-z0-9]{2,6})/i;
const roomTypeLabel = (_roomType) => "";
const isDirectChatRoom = (room) => Boolean(room?.isPrivate) && Array.isArray(room?.members) && room.members.length === 2;
const canManageRoomTheme = (room, userId) => {
  if (!room || !userId) return false;
  if (room.isPrivate) return true;
  const adminCandidates = [
    room.admin?._id,
    room.admin?.id,
    room.admin,
    room.owner?._id,
    room.owner?.id,
    room.owner,
    room.creator?._id,
    room.creator?.id,
    room.creator,
    room.createdBy?._id,
    room.createdBy?.id,
    room.createdBy,
    room.createdById
  ].filter(Boolean);
  return adminCandidates.some((candidate) => String(candidate) === String(userId));
};

const getRoomIdentity = (room, currentUserId) => {
  if (!room) {
    return {
      displayName: "Conversation",
      description: "chat",
      avatarUrl: ""
    };
  }

  if (isDirectChatRoom(room)) {
    const otherMember = room.members.find((member) => (member?._id || member?.id) !== currentUserId);
    const displayName = otherMember?.displayName || otherMember?.username || room.name || "Direct chat";
    return {
      displayName,
      description: otherMember?.username ? `@${otherMember.username}` : "Direct chat",
      avatarUrl: toAttachmentUrl(otherMember?.avatarUrl)
    };
  }

  return {
    displayName: room.name || "Conversation",
    description: room.description || "chat",
    avatarUrl: toAttachmentUrl(room.avatarUrl)
  };
};

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "WV";

const getMemberAvatarUrl = (member) =>
  toAttachmentUrl(
    member?.avatarUrl ||
      member?.avatar ||
      member?.profile?.avatarUrl ||
      member?.photoUrl ||
      member?.photoURL ||
      member?.user?.avatarUrl ||
      member?.user?.avatar ||
      ""
  );

const getMood = (status = "") => {
  const value = String(status).toLowerCase();
  if (value.includes("busy") || value.includes("dnd")) return "focus";
  if (value.includes("away") || value.includes("idle")) return "drift";
  return "live";
};

const getRoomPreview = (room) => {
  const content =
    room.lastMessage?.content ||
    room.lastMessage?.text ||
    room.lastMessageText ||
    room.lastMessagePreview ||
    room.description ||
    "Start the wave";
  return String(content).trim();
};

const getSmartReplies = (preview) => {
  const text = String(preview || "").toLowerCase();
  if (text.includes("meeting") || text.includes("call")) return ["On my way", "Send details"];
  if (text.includes("file") || text.includes("design") || text.includes("review")) return ["Looks good", "I will update"];
  if (text.includes("where") || text.includes("when")) return ["In 10 mins", "Share location"];
  return ["Sounds good", "Let us do it"];
};

const getDeliveryState = (room, unread) => {
  if (unread > 0) return "delivered";
  if (room.lastMessage?.seen || room.lastMessage?.seenBy?.length) return "seen";
  return "delivered";
};

const isImageAttachment = (file) => {
  if (!file) return false;
  if (isImage(file.mimeType || "")) return true;
  return IMAGE_FILE_EXT_PATTERN.test(String(file.fileName || file.url || ""));
};

const shouldHideAttachmentEcho = (content, attachments) => {
  const text = String(content || "").trim();
  if (!text || !Array.isArray(attachments) || !attachments.length) return false;
  const normalized = text.toLowerCase();
  const mirrorsAttachment = attachments.some((file) => {
    const url = String(file?.url || "").toLowerCase();
    const name = String(file?.fileName || "").toLowerCase();
    return (url && normalized === url) || (name && normalized === name);
  });
  if (mirrorsAttachment) return true;
  return URL_LIKE_PATTERN.test(text) && !/\s/.test(text);
};

function Icon({ children, size = 18 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}

function WaveLogo() {
  return (
    <span className="wavvy-wave" aria-hidden="true">
      <span />
      <span />
      <span />
      <span />
    </span>
  );
}

function DeliveryIcon({ state }) {
  if (state === "seen") {
    return (
      <span className="mchat-delivery seen" aria-label="Seen">
        <Icon size={14}>
          <path d="M5 12.5l3 3 4-5" />
          <path d="M11 12.5l3 3 5-6" />
        </Icon>
      </span>
    );
  }
  return (
    <span className="mchat-delivery" aria-label="Delivered">
      <Icon size={14}>
        <path d="M5 12.5l3 3 4-5" />
      </Icon>
    </span>
  );
}

export default function MobileChatApp({
  user,
  connected,
  rooms,
  activeRoom,
  unreadByRoom,
  messages,
  roomUsers,
  roomMedia,
  loadingRoomMedia,
  theme,
  onThemeChange,
  onSelectRoom,
  onCreateRoom,
  onJoinRoom,
  onSendMessage,
  onToggleReaction,
  onDeleteMessage,
  onLeaveRoom,
  onDeleteRoom,
  onOpenRoomInfo,
  onLogout
}) {
  const [screen, setScreen] = useState("home");
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [messageText, setMessageText] = useState("");
  const [messageFiles, setMessageFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [roomInfoOpen, setRoomInfoOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [reactionTarget, setReactionTarget] = useState(null);
  const [hiddenRooms, setHiddenRooms] = useState([]);
  const [pinnedRooms, setPinnedRooms] = useState([]);
  const [mutedRooms, setMutedRooms] = useState([]);
  const [roomThemes, setRoomThemes] = useState({});
  const [roomThemePickerRoomId, setRoomThemePickerRoomId] = useState("");
  const [smartPreviewRooms, setSmartPreviewRooms] = useState({});
  const [swipeRoom, setSwipeRoom] = useState({ id: null, x: 0, startX: 0 });
  const [quickActionsRoom, setQuickActionsRoom] = useState(null);
  const [fabOpen, setFabOpen] = useState(false);
  const [replyFlashRoomId, setReplyFlashRoomId] = useState(null);
  const [pressedRoomId, setPressedRoomId] = useState(null);
  const [mediaViewer, setMediaViewer] = useState({ open: false, items: [], activeIndex: 0 });
  const [keyboardOffset, setKeyboardOffset] = useState(0);

  const fileRef = useRef(null);
  const cameraFileRef = useRef(null);
  const listRef = useRef(null);
  const longPressRef = useRef(null);
  const roomLongPressRef = useRef(null);
  const messageTouchRef = useRef({ startX: 0, moved: false });
  const searchInputRef = useRef(null);
  const suppressRoomClickRef = useRef(false);

  const mood = getMood(user?.status || user?.presence || "");
  const currentUserAvatar = toAttachmentUrl(user?.avatarUrl);
  const currentUserName = user?.displayName || user?.username || "You";
  const hasComposerDraft = Boolean(messageText.trim()) || messageFiles.length > 0;

  useEffect(() => {
    if (!activeRoom) return;
    setScreen("chat");
    setFabOpen(false);
  }, [activeRoom]);

  useEffect(() => {
    if (screen !== "chat" || !listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, screen]);

  useEffect(() => {
    if (!searchExpanded) return;
    const timer = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 120);
    return () => window.clearTimeout(timer);
  }, [searchExpanded]);

  useEffect(() => {
    return () => {
      window.clearTimeout(longPressRef.current);
      window.clearTimeout(roomLongPressRef.current);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return undefined;

    const viewport = window.visualViewport;
    const updateKeyboardOffset = () => {
      const rawOffset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      setKeyboardOffset(rawOffset > 80 ? rawOffset : 0);
    };

    updateKeyboardOffset();
    viewport.addEventListener("resize", updateKeyboardOffset);
    viewport.addEventListener("scroll", updateKeyboardOffset);

    return () => {
      viewport.removeEventListener("resize", updateKeyboardOffset);
      viewport.removeEventListener("scroll", updateKeyboardOffset);
    };
  }, []);

  useEffect(() => {
    if (screen !== "chat" || !listRef.current) return;
    if (!keyboardOffset) return;
    requestAnimationFrame(() => {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    });
  }, [keyboardOffset, screen]);

  const mobileShellStyle = useMemo(
    () => ({ "--mobile-keyboard-offset": `${keyboardOffset}px` }),
    [keyboardOffset]
  );

  const filteredRooms = useMemo(() => {
    const base = rooms.filter((room) => !hiddenRooms.includes(room._id));
    const searched = base.filter((room) => `${room.name} ${room.description || ""}`.toLowerCase().includes(searchQuery.toLowerCase()));
    const pinnedSet = new Set(pinnedRooms);
    return searched.sort((a, b) => {
      const aPinned = pinnedSet.has(a._id);
      const bPinned = pinnedSet.has(b._id);
      if (aPinned !== bPinned) return aPinned ? -1 : 1;
      return new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime();
    });
  }, [rooms, hiddenRooms, searchQuery, pinnedRooms]);

  const activeNowUsers = useMemo(() => {
    const seen = new Set();
    const users = [];
    rooms.forEach((room) => {
      (room.members || []).forEach((member) => {
        const memberId = member._id || member.id;
        if (!memberId || memberId === user?.id || seen.has(memberId)) return;
        seen.add(memberId);
        users.push(member);
      });
    });
    return users.slice(0, 14);
  }, [rooms, user]);

  const openRoom = (room, isMember) => {
    if (isMember) onSelectRoom(room);
    else onJoinRoom(room._id);
  };

  const onChatTouchStart = (room, event) => {
    const startX = event.touches[0].clientX;
    setPressedRoomId(room._id);
    setSwipeRoom({ id: room._id, x: 0, startX });
    window.clearTimeout(roomLongPressRef.current);
    roomLongPressRef.current = window.setTimeout(() => {
      setQuickActionsRoom(room);
      setPressedRoomId(null);
      setSwipeRoom({ id: null, x: 0, startX: 0 });
    }, 420);
  };

  const onChatTouchMove = (event) => {
    if (!swipeRoom.id) return;
    const dx = event.touches[0].clientX - swipeRoom.startX;
    if (Math.abs(dx) > 10) {
      window.clearTimeout(roomLongPressRef.current);
    }
    const bounded = Math.max(-98, Math.min(98, dx));
    setSwipeRoom((prev) => ({ ...prev, x: bounded }));
  };

  const onChatTouchEnd = (room, isMember) => {
    window.clearTimeout(roomLongPressRef.current);
    setPressedRoomId(null);
    if (!swipeRoom.id) return;

    if (swipeRoom.x > 68) {
      suppressRoomClickRef.current = true;
      setReplyFlashRoomId(room._id);
      window.setTimeout(() => setReplyFlashRoomId(null), 1200);
      openRoom(room, isMember);
    } else if (swipeRoom.x < -68) {
      suppressRoomClickRef.current = true;
      setHiddenRooms((prev) => (prev.includes(room._id) ? prev : [...prev, room._id]));
    }

    setSwipeRoom({ id: null, x: 0, startX: 0 });
  };

  const startLongPress = (message) => {
    window.clearTimeout(longPressRef.current);
    longPressRef.current = window.setTimeout(() => {
      setReactionTarget(message._id);
    }, 380);
  };

  const clearLongPress = () => {
    window.clearTimeout(longPressRef.current);
  };

  const onMessageTouchStart = (event, message) => {
    messageTouchRef.current = { startX: event.touches[0].clientX, moved: false };
    startLongPress(message);
  };

  const onMessageTouchMove = (event) => {
    const dx = event.touches[0].clientX - messageTouchRef.current.startX;
    if (Math.abs(dx) > 16) {
      messageTouchRef.current.moved = true;
      clearLongPress();
    }
  };

  const onMessageTouchEnd = (event, message) => {
    clearLongPress();
    const dx = event.changedTouches[0].clientX - messageTouchRef.current.startX;
    if (Math.abs(dx) > 70) {
      setReplyTo(message);
    }
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    if (!messageText.trim() && !messageFiles.length) return;
    setSending(true);
    try {
      await onSendMessage({ content: messageText, files: messageFiles });
      setMessageText("");
      setMessageFiles([]);
      setReplyTo(null);
      if (fileRef.current) fileRef.current.value = "";
      if (cameraFileRef.current) cameraFileRef.current.value = "";
    } finally {
      setSending(false);
    }
  };

  const openMediaViewer = (items, startIndex = 0) => {
    if (!Array.isArray(items) || !items.length) return;
    const safeIndex = Math.max(0, Math.min(startIndex, items.length - 1));
    setMediaViewer({ open: true, items, activeIndex: safeIndex });
  };

  const closeMediaViewer = () => {
    setMediaViewer({ open: false, items: [], activeIndex: 0 });
  };

  const selectRoomTheme = (roomId, themeId) => {
    if (!roomId || !themeId) return;
    setRoomThemes((prev) => ({ ...prev, [roomId]: themeId }));
  };

  const renderQuickActions = () => {
    if (!quickActionsRoom) return null;
    const quickRoomIdentity = getRoomIdentity(quickActionsRoom, user?.id);
    const canManageTheme = canManageRoomTheme(quickActionsRoom, user?.id);
    return (
      <>
        <button type="button" className="mquick-overlay" aria-label="Close quick actions" onClick={() => setQuickActionsRoom(null)} />
        <section className="mquick-sheet" aria-label="Quick actions">
          <h4>{quickRoomIdentity.displayName}</h4>
          <p>Quick controls</p>
          <div className="mquick-actions">
            <button type="button" onClick={() => setPinnedRooms((prev) => prev.includes(quickActionsRoom._id) ? prev.filter((id) => id !== quickActionsRoom._id) : [quickActionsRoom._id, ...prev])}>
              {pinnedRooms.includes(quickActionsRoom._id) ? "Unpin" : "Pin"}
            </button>
            <button type="button" onClick={() => setMutedRooms((prev) => prev.includes(quickActionsRoom._id) ? prev.filter((id) => id !== quickActionsRoom._id) : [...prev, quickActionsRoom._id])}>
              {mutedRooms.includes(quickActionsRoom._id) ? "Unmute" : "Mute"}
            </button>
            {canManageTheme ? <button type="button" onClick={() => setRoomThemePickerRoomId(quickActionsRoom._id)}>Switch Theme</button> : null}
            <button type="button" onClick={() => setSmartPreviewRooms((prev) => ({ ...prev, [quickActionsRoom._id]: !(prev[quickActionsRoom._id] ?? true) }))}>
              Toggle AI Preview
            </button>
            <button type="button" className="danger" onClick={() => setHiddenRooms((prev) => prev.includes(quickActionsRoom._id) ? prev : [...prev, quickActionsRoom._id])}>Archive</button>
            <button type="button" className="danger" onClick={() => onDeleteRoom(quickActionsRoom._id)}>Delete</button>
          </div>
        </section>
        {roomThemePickerRoomId ? (
          <section className="mquick-sheet mtheme-picker-sheet" aria-label="Conversation theme picker">
            <h4>Choose Theme</h4>
            <p>Select a Wavvy theme for this conversation.</p>
            <div className="theme-grid">
              {THEMES.map((themeOption) => (
                <button
                  key={`room-theme-${themeOption.id}`}
                  type="button"
                  className={roomThemes[roomThemePickerRoomId] === themeOption.id ? "theme-chip active" : "theme-chip"}
                  onClick={() => {
                    selectRoomTheme(roomThemePickerRoomId, themeOption.id);
                    setRoomThemePickerRoomId("");
                  }}
                >
                  {themeOption.label}
                </button>
              ))}
            </div>
            <button type="button" className="ghost-btn mobile-ghost" onClick={() => setRoomThemePickerRoomId("")}>Close</button>
          </section>
        ) : null}
      </>
    );
  };

  if (screen === "settings") {
    return (
      <main className={`mobile-shell mobile-settings-shell mood-${mood}`} style={mobileShellStyle}>
        <header className="mobile-header mobile-glass-bar">
          <button type="button" className="micon-btn" onClick={() => setScreen("home")} aria-label="Back">
            <Icon size={17}><path d="M15 18l-6-6 6-6" /></Icon>
          </button>
          <h2>Settings</h2>
          <span className="mghost-slot" />
        </header>

        <section className="mobile-settings-content">
          <section className="mcard glass-card">
            <h3>Theme</h3>
            <div className="theme-grid">
              {THEMES.map((themeOption) => (
                <button key={themeOption.id} type="button" className={themeOption.id === theme ? "theme-chip active" : "theme-chip"} onClick={() => onThemeChange(themeOption.id)}>
                  {themeOption.label}
                </button>
              ))}
            </div>
          </section>

          <section className="mcard glass-card">
            <h3>Profile</h3>
            <div className="msettings-items">
              <div className="msettings-item">
                <strong>{user?.displayName || user?.username || "User"}</strong>
                <small>{user?.email || "No email set"}</small>
              </div>
            </div>
          </section>

          <section className="mcard glass-card">
            <h3>Edit Profile</h3>
            <div className="msettings-items">
              <div className="msettings-item">
                <strong>Profile Photo</strong>
                <small>Update avatar and display style</small>
              </div>
              <div className="msettings-item">
                <strong>Bio and Username</strong>
                <small>Customize profile details</small>
              </div>
            </div>
          </section>

          <section className="mcard glass-card">
            <h3>App Preferences</h3>
            <div className="msettings-items">
              <div className="msettings-item">
                <strong>Notifications</strong>
                <small>Message, mention, and call alerts</small>
              </div>
              <div className="msettings-item">
                <strong>Language</strong>
                <small>App language and regional format</small>
              </div>
              <div className="msettings-item">
                <strong>Connection</strong>
                <small>{connected ? "Connected" : "Reconnecting..."}</small>
              </div>
            </div>
          </section>

          <section className="mcard glass-card">
            <h3>Privacy & Safety</h3>
            <div className="msettings-items">
              <div className="msettings-item">
                <strong>Privacy Controls</strong>
                <small>Who can message and find you</small>
              </div>
              <div className="msettings-item">
                <strong>Blocked Users</strong>
                <small>Manage blocked accounts</small>
              </div>
              <div className="msettings-item">
                <strong>Security</strong>
                <small>Account safety and sessions</small>
              </div>
            </div>
          </section>

          <section className="mcard glass-card">
            <button type="button" className="danger-btn mobile-danger" onClick={onLogout}>Logout</button>
          </section>
        </section>
      </main>
    );
  }

  if (screen === "chat" && activeRoom) {
    const roomIdentity = getRoomIdentity(activeRoom, user?.id);
    const activeRoomTheme = roomThemes[activeRoom._id] || theme;
    const activeRoomCanManageTheme = canManageRoomTheme(activeRoom, user?.id);
    return (
      <main className={`mobile-shell mobile-chat-shell mood-${mood}`} style={mobileShellStyle}>
        <header className="mobile-chat-head mobile-glass-bar">
          <button type="button" className="micon-btn" onClick={() => setScreen("home")} aria-label="Back">
            <Icon size={17}><path d="M15 18l-6-6 6-6" /></Icon>
          </button>

          <button type="button" className="mroom-head" onClick={async () => {
            setRoomInfoOpen(true);
            await onOpenRoomInfo();
          }}>
            {roomIdentity.avatarUrl ? <img src={roomIdentity.avatarUrl} alt={roomIdentity.displayName} className="mavatar small" /> : <span className="mavatar small">{getInitials(roomIdentity.displayName)}</span>}
            <span>
              <strong>{roomIdentity.displayName}</strong>
              <small>{roomIdentity.description || "Fluid chat"}</small>
              {roomTypeLabel(activeRoom.roomType) ? <small>{roomTypeLabel(activeRoom.roomType)}</small> : null}
            </span>
          </button>

          <button type="button" className="micon-btn" aria-label="Call">
            <Icon size={16}><path d="M5 4h4l2 5-2 2a13 13 0 006 6l2-2 5 2v4a2 2 0 01-2 2A17 17 0 013 6a2 2 0 012-2z" /></Icon>
          </button>
          <button type="button" className="micon-btn" aria-label="More" onClick={async () => {
            setRoomInfoOpen(true);
            await onOpenRoomInfo();
          }}>
            <Icon size={16}><circle cx="5" cy="12" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="19" cy="12" r="1.5" /></Icon>
          </button>
        </header>

        <section className="mobile-message-list" ref={listRef}>
          {messages.map((message) => {
            const own = (message.user?._id || message.user?.id) === user?.id;
            if (message.system) {
              return <div key={message._id || message.createdAt} className="msystem">{message.content}</div>;
            }
            return (
              <article
                key={message._id || message.createdAt}
                className={own ? "mbubble own" : "mbubble"}
                onTouchStart={(event) => onMessageTouchStart(event, message)}
                onTouchMove={onMessageTouchMove}
                onTouchEnd={(event) => onMessageTouchEnd(event, message)}
              >
                <div className="mbubble-head">
                  <strong>{message.user?.username || "Unknown"}</strong>
                  <small>{formatTime(message.createdAt)}</small>
                </div>
                {(() => {
                  const attachments = Array.isArray(message.attachments) ? message.attachments : [];
                  const imageAttachments = attachments.filter((file) => isImageAttachment(file));
                  const fileAttachments = attachments.filter((file) => !isImageAttachment(file));
                  const hideAttachmentEcho = shouldHideAttachmentEcho(message.content, attachments);
                  const renderedContent = hideAttachmentEcho ? "" : message.content;
                  const previewImages = imageAttachments.slice(0, 4);
                  const hiddenCount = imageAttachments.length - previewImages.length;
                  const senderName = message.user?.username || "User";

                  return (
                    <>
                      {renderedContent ? <p>{renderedContent}</p> : null}

                      {imageAttachments.length ? (
                        <div className={`mimage-grid mimage-grid-${Math.min(previewImages.length, 4)}`}>
                          {previewImages.map((file, imageIndex) => (
                            <button
                              key={`${file.url}-${file.fileName}-${imageIndex}`}
                              type="button"
                              className="mimage-tile"
                              onClick={() => openMediaViewer(imageAttachments, imageIndex)}
                              aria-label={`Open image ${imageIndex + 1} of ${imageAttachments.length}`}
                            >
                              <img src={toAttachmentUrl(file.url)} alt={file.fileName || `Image ${imageIndex + 1}`} />
                              {imageIndex === previewImages.length - 1 && hiddenCount > 0 ? <span className="mimage-more">+{hiddenCount}</span> : null}
                            </button>
                          ))}
                        </div>
                      ) : null}

                      {!renderedContent && imageAttachments.length > 1 ? (
                        <small className="mmedia-sent-note">{own ? `You sent ${imageAttachments.length} photos` : `${senderName} sent ${imageAttachments.length} photos`}</small>
                      ) : null}

                      {fileAttachments.map((file) => (
                        <a key={`${file.url}-${file.fileName}`} className="mfile mfile-link" href={toAttachmentUrl(file.url)} target="_blank" rel="noreferrer">
                          <span>{file.fileName || "Attachment"}</span>
                        </a>
                      ))}
                    </>
                  );
                })()}
                {reactionTarget === message._id ? (
                  <div className="mreact-pop">
                    {reactionChoices.map((emoji) => (
                      <button key={emoji} type="button" onClick={() => {
                        onToggleReaction(message._id, emoji);
                        setReactionTarget(null);
                      }}>{emoji}</button>
                    ))}
                    <button type="button" className="mreact-delete" onClick={() => {
                      onDeleteMessage(message._id);
                      setReactionTarget(null);
                    }}>
                      Delete
                    </button>
                  </div>
                ) : null}
              </article>
            );
          })}
        </section>

        {replyTo ? (
          <div className="mreply-preview">
            <span>Replying to {replyTo.user?.username}</span>
            <button type="button" onClick={() => setReplyTo(null)}>Cancel</button>
          </div>
        ) : null}

        <form className={`mobile-input-bar compose-bar${hasComposerDraft ? " typing" : ""}`} onSubmit={sendMessage}>
          <button type="button" className="mobile-compose-camera" onClick={() => cameraFileRef.current?.click()} aria-label="Open camera">
            <Icon size={22}><path d="M4 8h4l2-2h4l2 2h4v10H4z" /><circle cx="12" cy="13" r="3.2" /></Icon>
          </button>
          <input
            type="text"
            value={messageText}
            onChange={(event) => setMessageText(event.target.value)}
            placeholder="Message..."
          />
          <input
            ref={cameraFileRef}
            type="file"
            className="file-input-hidden"
            accept="image/*"
            capture="environment"
            onChange={(event) => {
              const nextFiles = Array.from(event.target.files || []);
              if (!nextFiles.length) return;
              setMessageFiles((prev) => [...prev, ...nextFiles].slice(0, 5));
            }}
          />
          <input
            ref={fileRef}
            type="file"
            className="file-input-hidden"
            multiple
            onChange={(event) => {
              const nextFiles = Array.from(event.target.files || []);
              if (!nextFiles.length) return;
              setMessageFiles((prev) => [...prev, ...nextFiles].slice(0, 5));
            }}
          />
          {hasComposerDraft ? (
            <button type="submit" className="msend-btn compose-send" disabled={sending || (!messageText.trim() && !messageFiles.length)} aria-label="Send">
              <Icon size={18}><path d="M5 12h12" /><path d="M13 6l6 6-6 6" /></Icon>
            </button>
          ) : (
            <div className="mobile-compose-tools" aria-label="Compose tools">
              <button type="button" className="mobile-compose-tool" aria-label="Voice message">
                <Icon size={22}><path d="M12 4a3 3 0 0 1 3 3v4a3 3 0 1 1-6 0V7a3 3 0 0 1 3-3z" /><path d="M19 11a7 7 0 0 1-14 0" /><path d="M12 18v3" /></Icon>
              </button>
              <button type="button" className="mobile-compose-tool" onClick={() => fileRef.current?.click()} aria-label="Add image">
                <Icon size={22}><rect x="4" y="5" width="16" height="14" rx="2" ry="2" /><circle cx="9" cy="10" r="1.5" /><path d="M20 15l-4.5-4.5L7 19" /></Icon>
              </button>
              <button type="button" className="mobile-compose-tool" aria-label="Emoji">
                <Icon size={22}><circle cx="12" cy="12" r="8" /><path d="M9 10h.01" /><path d="M15 10h.01" /><path d="M9 14c1 1.3 2 2 3 2s2-.7 3-2" /></Icon>
              </button>
            </div>
          )}
        </form>

        <section className={roomInfoOpen ? "mroom-sheet open" : "mroom-sheet"}>
          <div className="msheet-handle" onClick={() => setRoomInfoOpen(false)} />
          <div className="msheet-head">
            {roomIdentity.avatarUrl ? <img src={roomIdentity.avatarUrl} alt={roomIdentity.displayName} className="mavatar large" /> : <span className="mavatar large">{getInitials(roomIdentity.displayName)}</span>}
            <h3>{roomIdentity.displayName}</h3>
            <p>{roomIdentity.description || "Fluid chat room"}</p>
          </div>
          <div className="msheet-block">
            <h4>Members</h4>
            <div className="msheet-members">
              {roomUsers.map((member) => (
                <div key={member.id || member._id} className="msheet-member">
                  <span className={member.online ? "dot on" : "dot"} />
                  <span>{member.displayName || member.username}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="msheet-block">
            <h4>Shared Media</h4>
            {loadingRoomMedia ? <p>Loading media...</p> : (
              <div className="msheet-media">
                {roomMedia.length ? roomMedia.map((media) => (
                  <a key={media.url} href={toAttachmentUrl(media.url)} target="_blank" rel="noreferrer">
                    <img src={toAttachmentUrl(media.url)} alt={media.fileName} />
                  </a>
                )) : <p>No media yet.</p>}
              </div>
            )}
          </div>
          <div className="msheet-block">
            <h4>Conversation Theme</h4>
            <p>Current: {THEMES.find((themeOption) => themeOption.id === activeRoomTheme)?.label || activeRoomTheme}</p>
            {activeRoomCanManageTheme ? (
              <>
                <button type="button" className="ghost-btn mobile-ghost" onClick={() => setRoomThemePickerRoomId(activeRoom._id)}>Switch Theme</button>
                {roomThemePickerRoomId === activeRoom._id ? (
                  <div className="theme-grid room-theme-grid">
                    {THEMES.map((themeOption) => (
                      <button
                        key={`sheet-theme-${themeOption.id}`}
                        type="button"
                        className={activeRoomTheme === themeOption.id ? "theme-chip active" : "theme-chip"}
                        onClick={() => {
                          selectRoomTheme(activeRoom._id, themeOption.id);
                          setRoomThemePickerRoomId("");
                        }}
                      >
                        {themeOption.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </>
            ) : (
              <p>Only room admin can change conversation theme.</p>
            )}
          </div>
          <button type="button" className="danger-btn mobile-danger" onClick={() => onLeaveRoom(activeRoom._id)}>Leave Room</button>
          <button type="button" className="ghost-btn mobile-ghost" onClick={() => onDeleteRoom(activeRoom._id)}>Delete Room</button>
        </section>
        {mediaViewer.open ? (
          <section className="mmedia-viewer" role="dialog" aria-modal="true" aria-label="Image gallery">
            <button type="button" className="mmedia-close" onClick={closeMediaViewer} aria-label="Close gallery">
              <Icon size={26}><path d="M6 6l12 12" /><path d="M18 6L6 18" /></Icon>
            </button>
            {mediaViewer.items[mediaViewer.activeIndex]?.url ? (
              <a
                className="mmedia-download"
                href={toAttachmentUrl(mediaViewer.items[mediaViewer.activeIndex].url)}
                download
                aria-label="Download image"
              >
                <Icon size={22}><path d="M12 3v12" /><path d="M8 11l4 4 4-4" /><path d="M4 21h16" /></Icon>
              </a>
            ) : null}
            <div className="mmedia-grid">
              {mediaViewer.items.map((file, index) => (
                <button
                  key={`${file.url}-${file.fileName}-${index}`}
                  type="button"
                  className={index === mediaViewer.activeIndex ? "mmedia-grid-tile active" : "mmedia-grid-tile"}
                  onClick={() => setMediaViewer((prev) => ({ ...prev, activeIndex: index }))}
                  aria-label={`Image ${index + 1}`}
                >
                  <img src={toAttachmentUrl(file.url)} alt={file.fileName || `Image ${index + 1}`} />
                </button>
              ))}
            </div>
          </section>
        ) : null}
      </main>
    );
  }

  return (
    <main className={`mobile-shell mobile-home-shell mood-${mood}`} style={mobileShellStyle}>
      <header className="mobile-header mobile-glass-bar">
        <div className="mobile-brand">
          <WaveLogo />
          <div>
            <h1>Wavvy</h1>
            <small>Future messaging</small>
          </div>
        </div>

        <div className="mobile-head-actions">
          <button
            type="button"
            className={searchExpanded ? "micon-btn active" : "micon-btn"}
            aria-label="Search"
            onClick={() => setSearchExpanded((prev) => !prev)}
          >
            <Icon size={16}><circle cx="11" cy="11" r="6" /><path d="M20 20l-3.5-3.5" /></Icon>
          </button>
          <button type="button" className="mprofile-btn" onClick={() => setScreen("settings")} aria-label="Settings">
            {currentUserAvatar ? <img src={currentUserAvatar} alt={currentUserName} /> : <span>{user?.username?.slice(0, 1)?.toUpperCase() || "U"}</span>}
          </button>
        </div>
      </header>

      <div className={searchExpanded ? "mobile-search-shell open" : "mobile-search-shell"}>
        <label className="mobile-search" aria-label="Search chats">
          <Icon size={16}><circle cx="11" cy="11" r="6" /><path d="M20 20l-3.5-3.5" /></Icon>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search waves"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </label>
      </div>

      <section className="mobile-active-now">
        <div className="section-head">
          <h3>Active Waves</h3>
        </div>

        <div className="mobile-active-row">
          <button type="button" className="mstory self" onClick={() => setScreen("settings")} aria-label="Open your profile settings">
            <span className="mstory-ring">
              {currentUserAvatar ? <img src={currentUserAvatar} alt={currentUserName} className="mstory-avatar-image" /> : <span className="mstory-avatar">{getInitials(currentUserName)}</span>}
              <em className="dot on" />
            </span>
            <small>You</small>
          </button>

          {activeNowUsers.map((member, index) => {
            const typing = Boolean(member.online) && index % 4 === 0;
            const memberAvatarUrl = getMemberAvatarUrl(member);
            const memberName = member.displayName || member.username || "User";
            return (
              <button key={member._id || member.id} type="button" className="mstory" aria-label={`Open ${memberName}`}>
                <span className="mstory-ring">
                  {memberAvatarUrl ? <img src={memberAvatarUrl} alt={memberName} className="mstory-avatar-image" /> : <span className="mstory-avatar">{getInitials(memberName)}</span>}
                  <em className={member.online ? "dot on" : "dot"} />
                </span>
                <small>{memberName}</small>
                {typing ? (
                  <span className="typing-wave" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </section>

      <section className="mobile-chat-list">
        {filteredRooms.map((room) => {
          const isMember = room.members?.some((member) => (member._id || member.id) === user?.id);
          const unread = unreadByRoom[room._id] || 0;
          const roomIdentity = getRoomIdentity(room, user?.id);
          const dragging = swipeRoom.id === room._id;
          const preview = getRoomPreview(room);
          const showSmart = smartPreviewRooms[room._id] ?? true;
          const smartReplies = getSmartReplies(preview);
          const deliveryState = getDeliveryState(room, unread);
          const pinned = pinnedRooms.includes(room._id);
          const muted = mutedRooms.includes(room._id);
          const active = activeRoom?._id === room._id;
          const replyGlow = replyFlashRoomId === room._id;
          return (
            <article key={room._id} className="mchat-item-wrap">
              <div className={swipeRoom.x > 14 && dragging ? "swipe-hint reply show" : "swipe-hint reply"}>Reply</div>
              <div className={swipeRoom.x < -14 && dragging ? "swipe-hint archive show" : "swipe-hint archive"}>Archive</div>

              <button
                type="button"
                className={`mchat-item${active ? " active" : ""}${muted ? " muted" : ""}${replyGlow ? " reply-glow" : ""}${pressedRoomId === room._id ? " pressed" : ""}`}
                onTouchStart={(event) => onChatTouchStart(room, event)}
                onTouchMove={onChatTouchMove}
                onTouchEnd={() => onChatTouchEnd(room, isMember)}
                onContextMenu={(event) => {
                  event.preventDefault();
                  setQuickActionsRoom(room);
                }}
                onClick={() => {
                  if (suppressRoomClickRef.current) {
                    suppressRoomClickRef.current = false;
                    return;
                  }
                  openRoom(room, isMember);
                }}
                style={dragging ? { transform: `translateX(${swipeRoom.x}px)` } : undefined}
              >
                <span className="mavatar-wrap">
                  {roomIdentity.avatarUrl ? <img src={roomIdentity.avatarUrl} alt={roomIdentity.displayName} className="mavatar" /> : <span className="mavatar gradient">{getInitials(roomIdentity.displayName)}</span>}
                  <em className={connected ? "dot on" : "dot"} />
                </span>

                <span className="mchat-copy">
                  <span className="mchat-title-line">
                    <strong>{roomIdentity.displayName}</strong>
                    {pinned ? <mark>PIN</mark> : null}
                    {muted ? <mark>MUTE</mark> : null}
                  </span>
                  <small className="mchat-preview">{preview}</small>
                  {showSmart ? (
                    <span className="smart-preview-row">
                      {smartReplies.map((reply) => (
                        <em key={`${room._id}-${reply}`}>{reply}</em>
                      ))}
                    </span>
                  ) : null}
                  {roomTypeLabel(room.roomType) ? <small>{roomTypeLabel(room.roomType)}</small> : null}
                </span>

                <span className="mchat-meta">
                  <small>{formatTime(room.updatedAt)}</small>
                  <DeliveryIcon state={deliveryState} />
                  {unread > 0 ? <em>{unread}</em> : null}
                </span>
              </button>
            </article>
          );
        })}
      </section>

      <div className={fabOpen ? "mobile-fab-cluster open" : "mobile-fab-cluster"}>
        <button type="button" className="mobile-fab mini action-group" onClick={onCreateRoom}>
          <Icon size={16}><path d="M8 12a3 3 0 100-6 3 3 0 000 6z" /><path d="M16 13a3 3 0 100-6 3 3 0 000 6z" /><path d="M3.5 19c.5-2.6 2.3-4 4.5-4s4 1.4 4.5 4" /><path d="M11.5 19c.4-2 1.6-3.2 3.5-3.6 2.2-.4 4.1.6 5 3.6" /></Icon>
          <span>Create group</span>
        </button>
        <button type="button" className="mobile-fab mini action-call" onClick={onCreateRoom}>
          <Icon size={16}><path d="M5 4h4l2 5-2 2a13 13 0 006 6l2-2 5 2v4a2 2 0 01-2 2A17 17 0 013 6a2 2 0 012-2z" /></Icon>
          <span>Start call</span>
        </button>

        <button type="button" className={fabOpen ? "mobile-fab trigger open" : "mobile-fab trigger"} onClick={() => setFabOpen((prev) => !prev)} aria-label="Actions">
          <Icon size={18}><path d="M12 5v14" /><path d="M5 12h14" /></Icon>
        </button>
      </div>

      <footer className="mobile-foot-status glass-pill">
        <span className={connected ? "dot on" : "dot"} />
        <small>{connected ? "Live and synced" : "Reconnecting wave..."}</small>
      </footer>

      {renderQuickActions()}
    </main>
  );
}
